import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DownloadSimpleIcon, PaperPlaneTiltIcon, WarningIcon } from '@phosphor-icons/react';
import { ApiError, Button, StatusChip } from '@smena/ui';
import { fetchTgDialogs, fetchTgSessions, importTgHistory } from './api';

/**
 * Импорт переписок из личного аккаунта УК в Telegram.
 *
 * Личный аккаунт - самый ценный источник формулировок: всё, что менеджер годами отвечал
 * гостям руками, лежит именно там. До этого шаг «Импорт переписок» знал только про Авито,
 * хотя личный аккаунт к тому моменту уже подключался в мастере на шаг раньше.
 *
 * Диалоги выбираются вручную и осознанно: в личном аккаунте лежит и переписка с бухгалтером,
 * и семейный чат. Тянуть всё подряд в базу знаний нельзя.
 */
export function TgHistoryImportCard() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const sessionsQ = useQuery({ queryKey: ['tg-sessions'], queryFn: fetchTgSessions });
  const session = sessionsQ.data?.items.find((s) => s.status === 'authorized');

  const dialogsQ = useQuery({
    queryKey: ['tg-dialogs', session?.sessionId],
    queryFn: () => fetchTgDialogs(session!.sessionId, 100),
    enabled: Boolean(session),
  });

  const importMut = useMutation({
    mutationFn: () => importTgHistory(session!.sessionId, [...selected]),
    onSuccess: (r) => {
      setSelected(new Set());
      if (r.imported) {
        toast.success(`В базу знаний добавлено переписок: ${r.imported} (${r.messages} сообщений)`);
      } else {
        toast.error('Ничего не добавлено - выбранные диалоги оказались пустыми');
      }
      if (r.skipped) toast.warning(`Пропущено коротких диалогов: ${r.skipped}`);
      for (const e of r.errors.slice(0, 3)) toast.error(e);
      void qc.invalidateQueries({ queryKey: ['kb-documents'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Импорт не удался'),
  });

  function toggle(peerId: number): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(peerId)) next.delete(peerId);
      else next.add(peerId);
      return next;
    });
  }

  const dialogs = dialogsQ.data?.items ?? [];

  return (
    <div className="rounded-md border border-line bg-panel2/40 p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <PaperPlaneTiltIcon weight="bold" className="size-4 text-accent-fg" /> Из личного Telegram
      </div>

      {!sessionsQ.data?.available ? (
        <div className="text-xs text-muted">
          Шлюз личных аккаунтов не поднят на этом контуре - импорт недоступен.
        </div>
      ) : !session ? (
        <div className="text-xs text-muted">
          Личный аккаунт не подключён. Подключите его на шаге «Каналы гостя» или в разделе
          «Интеграции» - после этого переписки можно будет забрать сюда.
        </div>
      ) : (
        <>
          <p className="mb-2 text-xs text-muted">
            Выберите переписки с гостями. Личные и рабочие чаты не с гостями брать не нужно -
            в базу знаний попадёт их содержимое.
          </p>

          {dialogsQ.isLoading ? (
            <div className="text-xs text-muted">Читаем список диалогов…</div>
          ) : dialogsQ.isError ? (
            <div className="flex items-start gap-2 rounded-md border border-red/40 bg-red/10 p-2 text-[11px] text-red">
              <WarningIcon weight="fill" className="mt-0.5 size-3.5 shrink-0" />
              <span>
                Не удалось получить диалоги:{' '}
                {dialogsQ.error instanceof ApiError ? dialogsQ.error.message : 'ошибка шлюза'}
              </span>
            </div>
          ) : dialogs.length === 0 ? (
            <div className="text-xs text-muted">В аккаунте не нашлось ни одного диалога.</div>
          ) : (
            <>
              <ul className="mb-2 flex max-h-64 flex-col gap-1 overflow-auto">
                {dialogs.map((d) => (
                  <li key={d.peerId}>
                    <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-panel2">
                      <input
                        type="checkbox"
                        checked={selected.has(d.peerId)}
                        onChange={() => toggle(d.peerId)}
                        className="size-4 shrink-0 accent-[var(--accent)]"
                      />
                      <span className="min-w-0 flex-1 truncate">{d.title}</span>
                      {d.username ? (
                        <span className="shrink-0 text-[11px] text-muted">@{d.username}</span>
                      ) : null}
                    </label>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  disabled={selected.size === 0 || importMut.isPending}
                  onClick={() => importMut.mutate()}
                >
                  <DownloadSimpleIcon weight="bold" className="size-4" />
                  {importMut.isPending ? 'Забираем…' : 'Забрать в базу знаний'}
                </Button>
                <StatusChip tone={selected.size ? 'accent' : 'muted'}>
                  выбрано: {selected.size}
                </StatusChip>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
